import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import clsx from 'clsx';
import { SmartLinkStatsMediaItem } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../../common/types';
import { mediaStatsMessages } from './mediaStatsMessages';
import { shareOf } from './mediaStatsRows';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media panel; each part uses its own slice of it */
import styles from './SMLMediaPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLMediaNumbersProps {
  item: SmartLinkStatsMediaItem;
  kind: 'image' | 'video';
  /** The page views of the window, for "88% of views". */
  pageViews: number;
  /** The length of the video in seconds, when known. */
  durationSec: number | null;
}

interface NumberCell {
  label: string;
  value: string;
  hint: string;
}

function percent(intl: IntlShape, share: number | null): string {
  return share === null ? '—' : intl.formatNumber(share, { style: 'percent', maximumFractionDigits: 0 });
}

/**
 * The four numbers of a Media block (design c1). A video: Seen, Plays, Avg watched, Sound on. An
 * image: Seen, Clicks, People. Under 576 px they go to two columns.
 */
const SMLMediaNumbersComponent: React.FC<SMLMediaNumbersProps> = ({ item, kind, pageViews, durationSec }) => {
  const intl = useIntl();

  const cells = useMemo<NumberCell[]>(() => {
    const count = (value: number) => intl.formatNumber(value, { notation: 'compact' });
    const seen: NumberCell = {
      label: intl.formatMessage(mediaStatsMessages.seen),
      value: count(item.seen),
      hint: intl.formatMessage(mediaStatsMessages.seenHint, { percent: percent(intl, shareOf(item.seen, pageViews)) }),
    };
    if (kind === 'image') {
      return [
        seen,
        {
          label: intl.formatMessage(mediaStatsMessages.clicks),
          value: count(item.clicks),
          hint: intl.formatMessage(mediaStatsMessages.clicksHint, {
            percent: percent(intl, shareOf(item.clicks, item.seen)),
          }),
        },
        {
          label: intl.formatMessage(mediaStatsMessages.people),
          value: count(item.visitors),
          hint: intl.formatMessage(mediaStatsMessages.peopleHint),
        },
      ];
    }
    const watched = item.avgWatchedPct;
    return [
      seen,
      {
        label: intl.formatMessage(mediaStatsMessages.plays),
        value: count(item.plays),
        hint: intl.formatMessage(mediaStatsMessages.playsHint, {
          percent: percent(intl, shareOf(item.plays, item.seen)),
        }),
      },
      {
        label: intl.formatMessage(mediaStatsMessages.avgWatched),
        value: watched === null ? '—' : percent(intl, watched / 100),
        hint:
          watched === null || durationSec === null
            ? ''
            : intl.formatMessage(mediaStatsMessages.avgWatchedHint, {
                seconds: intl.formatNumber((watched / 100) * durationSec, { maximumFractionDigits: 1 }),
                length: Math.round(durationSec),
              }),
      },
      {
        label: intl.formatMessage(mediaStatsMessages.soundOn),
        value: count(item.unmutes),
        hint: intl.formatMessage(mediaStatsMessages.soundOnHint, {
          percent: percent(intl, shareOf(item.unmutes, item.plays)),
        }),
      },
    ];
  }, [durationSec, intl, item, kind, pageViews]);

  return (
    <div className={clsx(styles.numbers, kind === 'image' && styles.numbersThree)}>
      {cells.map((cell) => (
        <div key={cell.label} className={styles.numberCell}>
          <span className={styles.numberLabel}>{cell.label}</span>
          <span className={styles.numberValue}>{cell.value}</span>
          <span className={styles.numberHint}>{cell.hint}</span>
        </div>
      ))}
    </div>
  );
};

export const SMLMediaNumbers = TypedMemo(SMLMediaNumbersComponent);
